import { FunctionComponent, ReactNode, useState } from 'react';
import { Locale } from 'enums/locale';
import { tr, enUS } from 'date-fns/locale';
import { useLocale } from 'hooks/useLocale';
import {
  Calendar,
  DateRangePicker,
  DateRangePickerProps,
} from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { dateService } from 'services/DateService';
import BLabeledInput from './BLabeledInput';

interface BDateRangePickerProps extends DateRangePickerProps {
  labelClasses: string;
  rangeLabels: {
    firstInput: string;
    lastInput: string;
  };
  nonRangeLabel: string;
  isRange: boolean;
  children: ReactNode;
}

const BDateRangePicker: FunctionComponent<BDateRangePickerProps> = ({
  ranges,
  onChange,
  rangeLabels,
  nonRangeLabel,
  labelClasses,
  isRange,
  children,
  ...props
}) => {
  const { locale } = useLocale();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

  const toggleDatePicker = () => {
    setIsDatePickerOpen((d) => !d);
  };

  const selectedLocale = (
    {
      [Locale.TR]: tr,
      [Locale.EN]: enUS,
    } as Record<Locale, typeof tr>
  )[locale];

  const formatInputValue = (date: Date | undefined) => {
    return date ? dateService.getReadableDate(date) : '';
  };

  const dateRangePickerProps = {
    className: 'overflow-hidden',
    color: 'var(--clr-primary-500)',
    showDateDisplay: false,
    locale: selectedLocale,
    showMonthAndYearPickers: false,
    rangeColors: ['var(--clr-primary-500)'],
    minDate: new Date(),
    ...props,
  };

  return (
    <div className="relative">
      <div className="grid items-center gap-4">
        <BLabeledInput
          labelClassName={labelClasses}
          label={
            isRange
              ? `${rangeLabels.firstInput} / ${rangeLabels.lastInput}`
              : nonRangeLabel
          }
          value={
            isRange
              ? `${formatInputValue(
                  ranges?.[0].startDate
                )} - ${formatInputValue(ranges?.[0].endDate)}`
              : formatInputValue(ranges?.[0].startDate)
          }
          readOnly
          className="cursor-pointer text-center"
          onClick={toggleDatePicker}
        />
        {/* {isRange ? (
          <BLabeledInput
            labelClassName={labelClasses}
            label={rangeLabels.lastInput}
            value={formatInputValue(ranges?.[0].endDate)}
            readOnly
            className="cursor-pointer"
            onClick={toggleDatePicker}
          />
        ) : null} */}
      </div>
      {isDatePickerOpen ? (
        <>
          <div
            className="fixed inset-0 z-50"
            onClick={toggleDatePicker}
            role="presentation"
          />
          <div className="absolute top-16 right-0 z-100 rounded-md border bg-white shadow-sm">
            {isRange ? (
              <DateRangePicker
                ranges={ranges}
                onChange={onChange}
                {...dateRangePickerProps}
              />
            ) : (
              <Calendar
                date={ranges?.[0].startDate}
                onChange={(date) => {
                  onChange?.({
                    state: {
                      startDate: date,
                      endDate: date,
                    },
                  });
                }}
                {...dateRangePickerProps}
              />
            )}
            <div>{children}</div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default BDateRangePicker;
